// src/services/apiKeyService.js
import { apiService } from './api';

class ApiKeyService {
    /**
     * Create a new API key
     * 
     * @param {Object} data - API key creation data
     * @param {string} data.name - Descriptive name for the API key
     * @param {string} data.userId - MongoDB ID of user who owns this key
     * @param {Array<string>} data.permissions - Array of allowed operations
     * @param {number} data.expiresInDays - Number of days until key expires (null for non-expiring)
     * @returns {Promise<Object>} - Created API key with secret
     */
    async createApiKey(data) {
        return apiService.fetchWithAuth('/api/keys', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    /**
     * Get all API keys
     * 
     * @returns {Promise<Array>} - List of API keys
     */
    async getApiKeys() {
        return apiService.fetchWithAuth('/api/keys');
    }

    /**
     * Get a specific API key by ID
     * 
     * @param {string} id - API key ID
     * @returns {Promise<Object>} - API key details
     */
    async getApiKey(id) {
        return apiService.fetchWithAuth(`/api/keys/${id}`);
    }

    /**
     * Update an API key
     * 
     * @param {string} id - API key ID
     * @param {Object} data - Update data
     * @returns {Promise<Object>} - Updated API key
     */
    async updateApiKey(id, data) {
        return apiService.fetchWithAuth(`/api/keys/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    /**
     * Revoke (deactivate) an API key
     * 
     * @param {string} id - API key ID
     * @returns {Promise<Object>} - Operation result
     */
    async revokeApiKey(id) {
        return apiService.fetchWithAuth(`/api/keys/${id}`, {
            method: 'DELETE',
        });
    }

    /**
     * Get API key usage logs
     * 
     * @param {string} id - API key ID
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number
     * @param {number} params.limit - Logs per page
     * @param {string} params.endpoint - Filter by endpoint
     * @param {string} params.method - Filter by HTTP method
     * @param {string} params.ip - Filter by IP address
     * @param {string} params.dateFrom - Filter by date from
     * @param {string} params.dateTo - Filter by date to
     * @returns {Promise<Object>} - API key logs with pagination
     */
    async getApiKeyLogs(id, params = { page: 1, limit: 20 }) {
        const queryParams = new URLSearchParams();

        // Add all provided parameters to query string
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });

        const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
        return apiService.fetchWithAuth(`/api/keys/${id}/logs${queryString}`);
    }
}

export const apiKeyService = new ApiKeyService(); 